import { cents } from '../money/index.js';
import { failure, success, type AwardTypeCode, type Cents, type Result } from '../types.js';

/** One person's tax as ADP Quick Calc shows it: the taxes on the gross and the net pay left. */
export interface TaxEntryFigures {
  readonly taxCents: Cents;
  readonly netPayCents: Cents;
}

const wholeCents = (value: number): boolean => Number.isSafeInteger(value) && value >= 0;

/**
 * APP-101 §2: the Stock Admin types the taxes and the net pay is the gross less them, because
 * Quick Calc is run with no deductions. The gross is the line's cash.
 */
export function taxEntryFigures(cashCents: number, taxCents: number): Result<TaxEntryFigures> {
  if (!wholeCents(cashCents) || !wholeCents(taxCents)) return failure('invalid_amount', 'taxCents');
  if (taxCents > cashCents) return failure('tax_above_cash', 'taxCents');
  return success({ taxCents: cents(taxCents), netPayCents: cents(cashCents - taxCents) });
}

/** The same figures when the net pay is typed over instead: the taxes are what's left. */
export function netToTax(cashCents: number, netPayCents: number): Result<TaxEntryFigures> {
  if (!wholeCents(cashCents) || !wholeCents(netPayCents))
    return failure('invalid_amount', 'netPayCents');
  if (netPayCents > cashCents) return failure('net_above_cash', 'netPayCents');
  return success({ taxCents: cents(cashCents - netPayCents), netPayCents: cents(netPayCents) });
}

/** Only a PAA line with cash has a tax figure: its cash can be taken as stock (PRD §5). */
export function needsTaxEntry(type: AwardTypeCode, cashCents: number): boolean {
  return type === 'PAA' && cashCents > 0;
}
